import { REQUEST_METHODS } from '@/constants/network'
import baseFetch from '@/network/baseFetch'
import type { FlightStatusQueryParams, FlightStatusResponse } from '@/containers/FlightStatusSrp/types'
import { HTTPResponseError } from '@/types/network/common'

const AEROWAVE_PATH = '/flights/flight-status/api/v1/guest/flights'

const UNWRAP_KEYS = ['data', 'response', 'result'] as const

/**
 * Aerowave responses may be T, { data: T }, or deeper nesting. Find the object that carries searchIntent.
 */
function findSearchPayload(raw: unknown, depth = 0): Record<string, unknown> | null {
    if (!raw || typeof raw !== 'object' || depth > 8) return null
    const obj = raw as Record<string, unknown>
    if (obj.searchIntent && typeof obj.searchIntent === 'object') return obj
    for (const key of UNWRAP_KEYS) {
        const inner = obj[key]
        if (inner && typeof inner === 'object') {
            const found = findSearchPayload(inner, depth + 1)
            if (found) return found
        }
    }
    return null
}

function extractCards(payload: Record<string, unknown>): unknown[] {
    const candidates = [payload.cards, payload.flights, payload.results, payload.flightCards]
    for (const c of candidates) {
        if (Array.isArray(c)) return c
    }
    return []
}

function normalizeResponseBody(raw: unknown): FlightStatusResponse | null {
    const payload = findSearchPayload(raw)
    if (!payload) return null
    const searchIntent = payload.searchIntent
    if (!searchIntent || typeof searchIntent !== 'object') return null
    const cards = extractCards(payload)
    return {
        searchIntent: searchIntent as FlightStatusResponse['searchIntent'],
        cards: cards as FlightStatusResponse['cards'],
        filterData: payload.filterData as FlightStatusResponse['filterData'],
    }
}

export const fetchFlightStatusApi = async (
    params: FlightStatusQueryParams
): Promise<{ status: number; data: FlightStatusResponse | null }> => {
    try {
        const query = new URLSearchParams()
        query.set('type', params.type)
        query.set('date', params.date)

        if (params.type === 'flight_number') {
            if (params.carrierCode) query.set('carrierCode', params.carrierCode)
            if (params.flightNumber) query.set('flightNumber', params.flightNumber)
        }

        if (params.type === 'route' || params.type === 'departure') {
            if (params.dep) query.set('dep', params.dep)
        }

        if (params.type === 'route' || params.type === 'arrival') {
            if (params.arr) query.set('arr', params.arr)
        }

        const url = `${AEROWAVE_PATH}?${query.toString()}`
        const raw = await baseFetch<unknown>({
            url,
            method: REQUEST_METHODS.GET,
        })
        const data = normalizeResponseBody(raw)
        if (!data) {
            return { status: 500, data: null }
        }
        return { status: 200, data }
    } catch (e) {
        if (e instanceof HTTPResponseError) {
            return { status: e.errorCode, data: null }
        }
        return { status: 500, data: null }
    }
}
